/* Greenleaf — search as you type, in the header search (desktop panel and the
 * phone menu). 29 Sep 2026, Andrew: "Yep 100%, lets do this".
 *
 * WHAT IT READS. Shopify's Predictive Search, as JSON: /search/suggest.json.
 * Products (lead photograph, title, price), shelves (collections) and pages,
 * in one request per pause in typing. No library, no section render: the JSON
 * already carries everything a row needs, and building a dozen rows in the
 * browser is cheaper than a second round of Liquid.
 *
 * WHAT IT DOES NOT REPLACE. The <form> still submits to /search, so Enter with
 * nothing highlighted, a script failure, or a blocked request all fall back to
 * the full results page. "See all results" at the foot of the list is the same
 * URL.
 *
 * NO GROUP LABELS. The rows need none — a photograph and a price say "piece",
 * a bare name says "shelf" — and small captions over lists are the thing
 * Andrew calls AI-looking (19 Sep 2026). The groups are split by a hairline.
 *
 * RANKING, the one thing it changes. Shopify ranks "emerald" by any field that
 * contains the word, so emerald-CUT diamond rings came first and the one
 * emerald ring was tenth (measured 29 Sep 2026). Ten are asked for and the
 * pieces whose own title contains every word typed are moved to the front;
 * the rest keep Shopify's order. Six are shown.
 *
 * KEYBOARD. The input is a combobox: ArrowDown / ArrowUp move through the
 * rows, Enter opens the highlighted one, Esc clears the list (a second Esc is
 * left to the header script, which closes the panel or the menu).
 *
 * PRICES are printed the way the rest of the site prints them — whole dollars
 * when there are no cents ("$1,995"), and "Price on request" at zero.
 */
(function () {
  'use strict';

  var MIN = 2, WAIT = 180, ASK = 10, SHOW = 6;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function money(v) {
    var n = parseFloat(v);
    if (!n) return 'Price on request';
    var whole = Math.round(n * 100) % 100 === 0;
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
  }

  function small(url) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=120';
  }

  function words(q) { return q.toLowerCase().split(/\s+/).filter(Boolean); }

  /* "ring" or "rings" on its own is the rings shelf (15 Oct 2026, the site review): Shopify's search finds "ring"
     inside "earring", so the results were 582 pieces for 375 rings. Same rule as the inline script at the top of
     the search results page (sections/gl-collection.liquid); keep the two equal. */
  function shelfFor(q) {
    var t = q.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    return (t === 'ring' || t === 'rings') ? root + 'collections/rings' : '';
  }

  function rank(products, q) {
    var w = words(q), first = [], rest = [];
    products.forEach(function (p) {
      var t = (p.title || '').toLowerCase();
      (w.every(function (x) { return t.indexOf(x) > -1; }) ? first : rest).push(p);
    });
    return first.concat(rest);
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function attach(input, n) {
    var form = input.form;
    if (!form || input.getAttribute('data-gl-suggest-on')) return;
    input.setAttribute('data-gl-suggest-on', '1');

    var list = el('div', 'suggest');
    list.id = 'gl-suggest-' + n;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Search suggestions');
    list.hidden = true;
    form.parentNode.insertBefore(list, form.nextSibling);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', list.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocomplete', 'off');

    var timer = null, ctrl = null, active = -1, last = '';

    function rows() { return Array.prototype.slice.call(list.querySelectorAll('[role="option"]')); }

    function mark(i) {
      var r = rows();
      r.forEach(function (x, j) { x.setAttribute('aria-selected', j === i ? 'true' : 'false'); });
      active = i;
      if (i > -1 && r[i]) { input.setAttribute('aria-activedescendant', r[i].id); r[i].scrollIntoView({ block: 'nearest' }); }
      else input.removeAttribute('aria-activedescendant');
    }

    /* THE RIPPLE (13 Oct 2026, Andrew's yes). Rows that are new since the last list fade and rise in, 30ms
       apart (styles in site.css, "SEARCH RIPPLE"). Rows already showing stay still; "See all" never moves.
       Only paint is staggered: every row is in the list and clickable at once. */
    var prev = {};
    function ripple() {
      var now = {}, n = 0;
      rows().forEach(function (r) {
        var h = (r.getAttribute('href') || '').split('?')[0];   // the suggest links carry ?_pos=…&_psq=<typed words>, which change on every keystroke
        now[h] = true;
        if (prev[h] || r.classList.contains('suggest__all')) return;
        r.classList.add('is-rip');
        r.style.setProperty('--i', Math.min(n++, 8));
      });
      prev = now;
    }

    function close() {
      prev = {};
      clearTimeout(skelTimer);
      list.removeAttribute('aria-busy');
      list.hidden = true; list.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      mark(-1);
    }

    function row(href, cls, n2) {
      var a = el('a', 'suggest__row ' + cls);
      a.href = href; a.id = list.id + '-o' + n2;
      a.setAttribute('role', 'option'); a.setAttribute('aria-selected', 'false');
      a.tabIndex = -1;
      return a;
    }

    function draw(q, r) {
      list.innerHTML = '';
      var k = 0;
      /* A question gets its answer first (assets/gl-search-answers.js): one line and a link to the guide, page or policy. */
      var ans = window.glAnswers ? window.glAnswers.find(q) : null;
      if (ans) {
        var ar = row(ans.url, 'suggest__row--answer', k++);
        ar.appendChild(el('span', 'suggest__ans', ans.a));
        ar.appendChild(el('span', 'suggest__ansl', ans.l));
        list.appendChild(ar);
      }
      var prods = rank(r.products || [], q).slice(0, SHOW);
      if (prods.length) {
        var g = el('div', 'suggest__grp');
        prods.forEach(function (p) {
          var a = row(p.url, 'suggest__row--piece', k++);
          var img = el('img', 'suggest__img');
          img.src = small(p.image || (p.featured_image && p.featured_image.url));
          img.alt = ''; img.width = 56; img.height = 56;
          a.appendChild(img);
          var t = el('span', 'suggest__t');
          t.appendChild(el('span', 'suggest__name', p.title));
          t.appendChild(el('span', 'suggest__price', money(p.price)));
          a.appendChild(t);
          g.appendChild(a);
        });
        list.appendChild(g);
      }
      var shelves = (r.collections || []).slice(0, 3), pages = (r.pages || []).slice(0, 2);
      if (shelves.length || pages.length) {
        var g2 = el('div', 'suggest__grp suggest__grp--links');
        /* A shelf and a page can share a name ("Engagement Rings" is both);
         * the shelf is kept, because it is where the pieces are. */
        var seen = {};
        shelves.concat(pages).filter(function (c) {
          var key = (c.title || '').toLowerCase().trim();
          if (seen[key]) return false; seen[key] = true; return true;
        }).forEach(function (c) {
          var a = row(c.url, 'suggest__row--link', k++);
          a.textContent = c.title;
          g2.appendChild(a);
        });
        list.appendChild(g2);
      }
      if (!k || (ans && k === 1)) list.appendChild(el('p', 'suggest__none', 'Nothing in the case matches that yet.'));
      var shelf = shelfFor(q);
      var all = row(shelf || root + 'search?q=' + encodeURIComponent(q), 'suggest__all', k++);
      all.textContent = shelf ? 'See all rings' : 'See all results for “' + q + '”';
      list.appendChild(all);
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      mark(-1);
      ripple();
    }

    // SKELETON (12 Oct 2026, "feels fast"). If the list is empty when a search goes out
    // and the answer takes longer than a blink (120 ms), three soft placeholder rows hold
    // the space, so the panel opens at once instead of appearing late. They are not
    // options (no role, no id), and draw() or close() replaces them. A list that is
    // already showing the previous words keeps them until the new answer lands.
    var skelTimer = null;
    function skeleton() {
      if (!list.hidden && list.querySelector('[role="option"]')) return;
      list.innerHTML = '';
      var g = el('div', 'suggest__grp suggest__skel');
      g.setAttribute('aria-hidden', 'true');
      for (var i = 0; i < 3; i++) {
        var r = el('div', 'suggest__row suggest__row--piece');
        r.appendChild(el('span', 'suggest__img'));
        var t = el('span', 'suggest__t');
        t.appendChild(el('span', 'suggest__bar'));
        t.appendChild(el('span', 'suggest__bar suggest__bar--short'));
        r.appendChild(t);
        g.appendChild(r);
      }
      list.appendChild(g);
      list.setAttribute('aria-busy', 'true');
      list.hidden = false;
    }

    function ask(q) {
      clearTimeout(skelTimer);
      skelTimer = setTimeout(skeleton, 120);
      if (ctrl) ctrl.abort();
      ctrl = ('AbortController' in window) ? new AbortController() : null;
      var url = root + 'search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page&resources[limit]=' + ASK +
        '&resources[limit_scope]=each&resources[options][unavailable_products]=last';
      fetch(url, ctrl ? { signal: ctrl.signal } : {}).then(function (res) {
        return res.ok ? res.json() : null;
      }).then(function (d) {
        if (input.value.trim() !== q) return;
        clearTimeout(skelTimer);
        list.removeAttribute('aria-busy');
        if (!d) { close(); return; }
        draw(q, (d.resources && d.resources.results) || {});
      }).catch(function (e) {
        // An aborted request is a newer search taking over: leave its skeleton alone.
        if (e && e.name === 'AbortError') return;
        clearTimeout(skelTimer);
        list.removeAttribute('aria-busy');
        close();
      });
    }

    // Enter, or the magnifier, on just "ring" or "rings" goes to the rings shelf, not the search results.
    form.addEventListener('submit', function (e) {
      var shelf = shelfFor(input.value);
      if (!shelf) return;
      e.preventDefault();
      window.location.href = shelf;
    });

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      // Any keystroke drops the highlighted row. The list on screen still
      // belongs to the PREVIOUS words until the new answer arrives, so Enter
      // must not open it: pick "emerald", type "diamond", press Enter before
      // the refresh, and it used to open the emerald piece (outside review,
      // 30 Sep 2026). With nothing highlighted, Enter submits the form and
      // searches for what is actually in the box.
      mark(-1);
      if (q.length < MIN) { last = ''; close(); return; }
      if (q === last) return;
      timer = setTimeout(function () { last = q; ask(q); }, WAIT);
    });

    input.addEventListener('keydown', function (e) {
      var r = rows();
      if (e.key === 'ArrowDown' && r.length) { e.preventDefault(); mark(active + 1 >= r.length ? 0 : active + 1); }
      else if (e.key === 'ArrowUp' && r.length) { e.preventDefault(); mark(active - 1 < 0 ? r.length - 1 : active - 1); }
      else if (e.key === 'Enter' && active > -1 && r[active]) { e.preventDefault(); window.location.href = r[active].href; }
      else if ((e.key === 'Escape' || e.key === 'Esc') && !list.hidden) { e.stopPropagation(); close(); }
    });

    list.addEventListener('mousemove', function (e) {
      var a = e.target.closest ? e.target.closest('[role="option"]') : null;
      if (a) mark(rows().indexOf(a));
    });
    /* A press inside the list must not blur the input before the click lands. */
    list.addEventListener('mousedown', function (e) { e.preventDefault(); });

    document.addEventListener('click', function (e) {
      if (!list.hidden && !list.contains(e.target) && e.target !== input) close();
    });
  }

  function init() {
    var ins = document.querySelectorAll('input[data-gl-suggest], #gl-q, #gl-q-mmenu');
    for (var i = 0; i < ins.length; i++) attach(ins[i], i);
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
